/**
 * Seller Panel Guard — requires seller role plus seller tier (admin bypass).
 */

import React, { useEffect } from 'react';
import { useFirebaseAuth } from '@/hooks/use-firebase-auth';
import { usePanelAccess } from '@/hooks/use-panel-access';
import { normalizeRoles } from '@/lib/normalize-user-data';
import { UpgradeModal } from '@/components/modals/UpgradeModal';
import { useLocation } from 'wouter';
import { useToast } from '@/hooks/use-toast';

interface SellerPanelGuardProps {
  children: React.ReactNode;
}

export function SellerPanelGuard({ children }: SellerPanelGuardProps) {
  const { user, isLoading } = useFirebaseAuth();
  const { subscriptionTier, subscriptionStatus, canAccessSellerPanel, loading: panelAccessLoading } = usePanelAccess();
  const [, navigate] = useLocation();
  const { toast } = useToast();

  // Redirect unauthenticated users to auth with explanation
  useEffect(() => {
    if (!isLoading && !user) {
      toast({
        title: "Sign in required",
        description: "Please sign in or create an account to access the Seller Panel.",
        variant: "destructive",
      });
      navigate("/auth?returnTo=/seller");
    }
  }, [isLoading, user]);

  // Wait for both auth and subscription data before making access decision
  if (isLoading || !user || panelAccessLoading) {
    return null;
  }

  const normalizedRoles = normalizeRoles(user);
  const hasSellerRoleAccess = normalizedRoles.includes('SELLER') || normalizedRoles.includes('PREMIUM_SELLER');
  const hasSellerAccess = canAccessSellerPanel;
  
  // Detailed console logging
  console.log('🛒 SellerPanelGuard Permission Check:', {
    action: 'SELLER_PANEL_ACCESS',
    RAW_USER_DATA: {
      subscriptionTier: subscriptionTier || undefined,
      roles: user?.roles,
      email: user?.email
    },
    PROCESSED_DATA: {
      subscriptionTier: subscriptionTier || 'FREE',
      hasSellerRoleAccess,
      userRoles: normalizedRoles,
      hasSellerAccess
    },
    reason: hasSellerAccess
      ? `✅ Access GRANTED - ${hasSellerRoleAccess ? "Seller role + seller tier" : "Admin"}`
      : `❌ Access DENIED - Need SELLER/PREMIUM_SELLER role and active seller tier`
  });

  // If user has access, show the content
  if (hasSellerAccess) {
    return <>{children}</>;
  }

  // No access - show upgrade modal for Free users
  return (
    <UpgradeModal
      isOpen={true}
      onClose={() => window.history.back()}
      onUpgrade={() => {}}
      requiredTier="Nobility"
      featureName="Seller Panel Access"
      userTier={subscriptionTier || 'Free'}
      userRoles={normalizedRoles}
      rawUserData={{
        subscriptionTier: subscriptionTier || undefined,
        roles: user?.roles,
        email: user?.email
      }}
    />
  );
}
